"use client";

/* eslint-disable @next/next/no-img-element */

import { Icons } from "@/components/icons";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { EventTwibbonResponse } from "@/lib/types/twibbon";
import { cn } from "@/lib/utils";
import {
  Copy,
  Camera,
  Check,
  Download,
  Image as ImageIcon,
  RefreshCw,
  Upload,
  Sparkles,
  Share2,
  ArrowLeft,
} from "lucide-react";
import Link from "next/link";
import { PointerEvent, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";

const EXPORT_SIZE = 1080;
const PREVIEW_SIZE = 720;
const MAX_UPLOAD_SIZE = 10 * 1024 * 1024;
const ACCEPTED_TYPES = ["image/png", "image/jpeg", "image/webp", "image/heic", "image/heif"];

type Transform = {
  offsetX: number;
  offsetY: number;
  zoom: number;
};

type LoadedPhoto = {
  url: string;
  image: HTMLImageElement;
  fileName: string;
};

type PointerDrag = {
  startX: number;
  startY: number;
  offsetX: number;
  offsetY: number;
};

function loadImage(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const image = new window.Image();

    image.crossOrigin = "anonymous";
    image.onload = () => resolve(image);
    image.onerror = () => reject(new Error("Image failed to load"));
    image.src = src;
  });
}

function clamp(value: number, min: number, max: number) {
  return Math.min(Math.max(value, min), max);
}

function drawPhotoWithFrame({
  canvas,
  photo,
  frame,
  transform,
  size,
}: {
  canvas: HTMLCanvasElement;
  photo: HTMLImageElement | null;
  frame: HTMLImageElement | null;
  transform: Transform;
  size: number;
}) {
  const context = canvas.getContext("2d");

  if (!context) {
    return;
  }

  canvas.width = size;
  canvas.height = size;
  context.clearRect(0, 0, size, size);

  if (photo) {
    const baseScale = Math.max(size / photo.naturalWidth, size / photo.naturalHeight);
    const drawWidth = photo.naturalWidth * baseScale * transform.zoom;
    const drawHeight = photo.naturalHeight * baseScale * transform.zoom;
    const drawX = (size - drawWidth) / 2 + transform.offsetX * (size / PREVIEW_SIZE);
    const drawY = (size - drawHeight) / 2 + transform.offsetY * (size / PREVIEW_SIZE);

    context.drawImage(photo, drawX, drawY, drawWidth, drawHeight);
  } else {
    const isDark =
      typeof document !== "undefined" && document.documentElement.classList.contains("dark");
    const tileColor1 = isDark ? "#1f2937" : "#f3f4f6";
    const tileColor2 = isDark ? "#111827" : "#e5e7eb";
    const tileSize = 20;
    for (let y = 0; y < size; y += tileSize) {
      for (let x = 0; x < size; x += tileSize) {
        context.fillStyle = (x / tileSize + y / tileSize) % 2 === 0 ? tileColor1 : tileColor2;
        context.fillRect(x, y, tileSize, tileSize);
      }
    }
  }

  if (frame) {
    context.drawImage(frame, 0, 0, size, size);
  }
}

function clampTransform(photo: HTMLImageElement, transform: Transform): Transform {
  const baseScale = Math.max(PREVIEW_SIZE / photo.naturalWidth, PREVIEW_SIZE / photo.naturalHeight);
  const drawWidth = photo.naturalWidth * baseScale * transform.zoom;
  const drawHeight = photo.naturalHeight * baseScale * transform.zoom;
  const maxX = Math.max(0, (drawWidth - PREVIEW_SIZE) / 2);
  const maxY = Math.max(0, (drawHeight - PREVIEW_SIZE) / 2);

  return {
    zoom: transform.zoom,
    offsetX: clamp(transform.offsetX, -maxX, maxX),
    offsetY: clamp(transform.offsetY, -maxY, maxY),
  };
}

function canNativeShare(file: File) {
  return (
    typeof navigator !== "undefined" &&
    Boolean(navigator.canShare?.({ files: [file] })) &&
    Boolean(navigator.share)
  );
}

function getFullUrl(url: string | null | undefined): string {
  if (!url) return "";

  if (url.startsWith("http://") || url.startsWith("https://")) {
    try {
      const parsed = new URL(url);
      if (
        parsed.hostname.includes("ticket.id") ||
        parsed.hostname.includes("mastermindevent.id")
      ) {
        return parsed.pathname;
      }
    } catch {}
    return url;
  }

  if (url.startsWith("data:") || url.startsWith("blob:")) {
    return url;
  }

  const cleanPath = url.startsWith("/") ? url.substring(1) : url;
  if (cleanPath.startsWith("storage/")) {
    return `/${cleanPath}`;
  }
  return `/storage/${cleanPath}`;
}

export function TwibbonCampaign({ initialData }: { initialData: EventTwibbonResponse }) {
  const [selectedTemplateId, setSelectedTemplateId] = useState(initialData.templates[0]?.id);
  const selectedTemplate = useMemo(
    () =>
      initialData.templates.find((template) => template.id === selectedTemplateId) ||
      initialData.templates[0] ||
      null,
    [initialData.templates, selectedTemplateId],
  );
  const [photo, setPhoto] = useState<LoadedPhoto | null>(null);
  const [frameImage, setFrameImage] = useState<HTMLImageElement | null>(null);
  const [isFrameLoading, setIsFrameLoading] = useState(false);
  const [transform, setTransform] = useState<Transform>({
    offsetX: 0,
    offsetY: 0,
    zoom: 1,
  });
  const [error, setError] = useState<string | null>(null);
  const [generatedUrl, setGeneratedUrl] = useState<string | null>(null);
  const [generatedBlob, setGeneratedBlob] = useState<Blob | null>(null);
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const cameraInputRef = useRef<HTMLInputElement | null>(null);
  const dragRef = useRef<PointerDrag | null>(null);
  const generatedUrlRef = useRef<string | null>(null);

  useEffect(() => {
    return () => {
      if (photo?.url) {
        URL.revokeObjectURL(photo.url);
      }
    };
  }, [photo?.url]);

  useEffect(() => {
    return () => {
      if (generatedUrlRef.current) {
        URL.revokeObjectURL(generatedUrlRef.current);
      }
    };
  }, []);

  useEffect(() => {
    let ignore = false;

    if (!selectedTemplate) {
      setIsFrameLoading(false);
      setFrameImage(null);
      return;
    }

    setIsFrameLoading(true);
    setFrameImage(null);
    loadImage(getFullUrl(selectedTemplate.frame_image_url))
      .then((image) => {
        if (!ignore) {
          setFrameImage(image);
          setIsFrameLoading(false);
        }
      })
      .catch(() => {
        if (!ignore) {
          setFrameImage(null);
          setIsFrameLoading(false);
          setError("Template tidak bisa dimuat. Coba refresh halaman.");
        }
      });

    setTransform({ offsetX: 0, offsetY: 0, zoom: 1 });
    setGeneratedUrl(null);
    setGeneratedBlob(null);

    return () => {
      ignore = true;
    };
  }, [selectedTemplate]);

  useEffect(() => {
    if (!canvasRef.current) {
      return;
    }

    drawPhotoWithFrame({
      canvas: canvasRef.current,
      photo: photo?.image ?? null,
      frame: frameImage,
      transform,
      size: PREVIEW_SIZE,
    });
  }, [frameImage, photo, transform]);

  const pageSlug = initialData.event.current_slug ?? initialData.event.slug;
  const frontendUrl = process.env.NEXT_PUBLIC_FRONTEND_URL.replace(/\/$/, "");
  const shareUrl = `${frontendUrl}/${pageSlug}/twibbon`;

  function resetGenerated() {
    if (generatedUrlRef.current) {
      URL.revokeObjectURL(generatedUrlRef.current);
      generatedUrlRef.current = null;
    }

    setGeneratedUrl(null);
    setGeneratedBlob(null);
  }

  async function handlePhotoSelected(file: File | undefined) {
    setError(null);

    if (!file) {
      return;
    }

    if (!ACCEPTED_TYPES.includes(file.type)) {
      setError("Gunakan foto PNG, JPG/JPEG, WEBP, atau HEIC.");
      return;
    }

    if (file.size > MAX_UPLOAD_SIZE) {
      setError("Ukuran foto maksimal 10 MB.");
      return;
    }

    const objectUrl = URL.createObjectURL(file);

    try {
      const image = await loadImage(objectUrl);

      setPhoto((previous) => {
        if (previous?.url) {
          URL.revokeObjectURL(previous.url);
        }

        return {
          url: objectUrl,
          image,
          fileName: file.name,
        };
      });
      setTransform({ offsetX: 0, offsetY: 0, zoom: 1 });
      resetGenerated();
    } catch {
      URL.revokeObjectURL(objectUrl);
      setError(
        file.type === "image/heic" || file.type === "image/heif"
          ? "Browser ini belum bisa membaca HEIC. Gunakan JPG, PNG, atau WEBP."
          : "Foto tidak bisa dibaca. Coba unggah file lain.",
      );
    }
  }

  function updateZoom(value: number) {
    if (!photo) {
      return;
    }

    resetGenerated();
    setTransform((current) =>
      clampTransform(photo.image, {
        ...current,
        zoom: Number(value),
      }),
    );
  }

  function resetTransform() {
    resetGenerated();
    setTransform({ offsetX: 0, offsetY: 0, zoom: 1 });
  }

  function startDrag(event: PointerEvent<HTMLCanvasElement>) {
    if (!photo) {
      return;
    }

    event.currentTarget.setPointerCapture(event.pointerId);
    dragRef.current = {
      startX: event.clientX,
      startY: event.clientY,
      offsetX: transform.offsetX,
      offsetY: transform.offsetY,
    };
  }

  function moveDrag(event: PointerEvent<HTMLCanvasElement>) {
    if (!photo || !dragRef.current) {
      return;
    }

    const next = clampTransform(photo.image, {
      ...transform,
      offsetX: dragRef.current.offsetX + event.clientX - dragRef.current.startX,
      offsetY: dragRef.current.offsetY + event.clientY - dragRef.current.startY,
    });

    resetGenerated();
    setTransform(next);
  }

  function stopDrag() {
    dragRef.current = null;
  }

  async function generateImage() {
    if (!photo || !frameImage) {
      setError("Pilih foto dulu sebelum generate Twibbon.");
      return;
    }

    const canvas = document.createElement("canvas");

    drawPhotoWithFrame({
      canvas,
      photo: photo.image,
      frame: frameImage,
      transform,
      size: EXPORT_SIZE,
    });

    const blob = await new Promise<Blob | null>((resolve) => {
      canvas.toBlob(resolve, "image/png", 1);
    });

    if (!blob) {
      setError("Twibbon gagal dibuat. Coba lagi.");
      return;
    }

    resetGenerated();

    const objectUrl = URL.createObjectURL(blob);
    generatedUrlRef.current = objectUrl;
    setGeneratedUrl(objectUrl);
    setGeneratedBlob(blob);
  }

  async function downloadImage() {
    if (!generatedBlob || !generatedUrl) {
      return;
    }

    const link = document.createElement("a");
    link.href = generatedUrl;
    link.download = `${initialData.event.slug}-twibbon.png`;
    document.body.append(link);
    link.click();
    link.remove();
  }

  async function nativeShareImage() {
    if (!generatedBlob) {
      return;
    }

    const file = new File([generatedBlob], `${initialData.event.slug}-twibbon.png`, {
      type: "image/png",
    });

    if (!canNativeShare(file)) {
      toast.error("Browser ini belum mendukung share gambar langsung.");
      return;
    }

    await navigator.share({
      files: [file],
      title: `Twibbon ${initialData.event.title}`,
      text: `Saya ikut mendukung ${initialData.event.title}`,
    });
  }

  async function shareLink() {
    try {
      if (navigator.share) {
        await navigator.share({
          title: `Twibbon ${initialData.event.title}`,
          text: "Buat Twibbon kamu di sini.",
          url: shareUrl,
        });
        return;
      }

      await navigator.clipboard.writeText(shareUrl);
      toast.success("Link Twibbon disalin.");
    } catch {
      toast.error("Link belum bisa dibagikan. Coba salin manual.");
    }
  }

  const generatedFile =
    generatedBlob && typeof File !== "undefined"
      ? new File([generatedBlob], "twibbon.png", { type: "image/png" })
      : null;
  const supportsNativeShare = generatedFile ? canNativeShare(generatedFile) : false;
  const generateDisabledReason = !photo
    ? "Pilih foto dulu."
    : isFrameLoading
      ? "Frame sedang dimuat."
      : !selectedTemplate || !frameImage
        ? "Frame belum siap. Coba pilih template lain atau refresh halaman."
        : null;

  return (
    <main className="mx-auto flex min-h-screen w-full max-w-[520px] flex-col gap-4 px-3 pb-6 pt-3 text-white dark:text-theme-dark lg:pb-8 lg:pt-8">
      {/* Back to event link */}
      <div className="flex items-center gap-2">
        <Link
          href={`/${pageSlug}`}
          className="inline-flex items-center gap-2 text-sm font-semibold text-theme-accent transition hover:opacity-80"
        >
          <ArrowLeft className="size-4" />
          Kembali ke event
        </Link>
      </div>

      <div className="flex flex-col gap-4">
        <div className="flex flex-col gap-6 rounded-2xl border border-white/10 bg-theme-black2 p-4 shadow-xl dark:border-theme-gray/20 dark:bg-white sm:p-6">
          {/* Canvas Wrapper */}
          <div className="relative aspect-square w-full overflow-hidden rounded-2xl border border-white/10 bg-[#0f141b] shadow-inner dark:border-theme-gray/20 dark:bg-[#f4f6f8]">
            {/* If we have a generated URL, show it as an image for easy saving */}
            {generatedUrl ? (
              <img
                src={generatedUrl}
                alt="Twibbon hasil generate"
                className="aspect-square w-full object-contain rounded-2xl animate-fade-in"
              />
            ) : (
              <canvas
                ref={canvasRef}
                className={cn(
                  "aspect-square w-full touch-none select-none",
                  photo ? "cursor-grab active:cursor-grabbing" : "cursor-default",
                )}
                aria-label="Preview Twibbon"
                onPointerDown={startDrag}
                onPointerMove={moveDrag}
                onPointerUp={stopDrag}
                onPointerCancel={stopDrag}
              />
            )}

            {/* Overlay checkered transparency indicator or camera icon when empty */}
            {!photo && !generatedUrl && (
              <div className="absolute inset-0 pointer-events-none flex flex-col items-center justify-center gap-2 bg-black/20 text-white/60 dark:text-black/40">
                <ImageIcon className="size-10 stroke-1 animate-pulse" />
                <span className="text-xs font-semibold">Foto akan tampil di sini</span>
              </div>
            )}
          </div>

          {/* Template Picker */}
          {initialData.templates.length > 1 && (
            <section className="flex flex-col gap-4 border-t border-white/10 pt-6 dark:border-theme-gray/20">
              <h2 className="text-base font-bold text-white dark:text-theme-black3">
                Pilih Desain Frame
              </h2>
              <div className="flex gap-4 overflow-x-auto pb-2 no-scrollbar">
                {initialData.templates.map((template) => {
                  const isSelected = template.id === selectedTemplate?.id;
                  return (
                    <button
                      key={template.id}
                      type="button"
                      className={cn(
                        "relative aspect-square w-24 shrink-0 overflow-hidden rounded-2xl border-2 bg-theme-black2 transition-all hover:scale-[1.03] dark:bg-theme-gray/10",
                        isSelected
                          ? "border-theme-accent shadow-lg shadow-theme-accent/30 scale-95"
                          : "border-white/10 hover:border-theme-accent/50 dark:border-theme-gray/20",
                      )}
                      aria-pressed={isSelected}
                      aria-label={`Pilih template ${template.name}`}
                      onClick={() => setSelectedTemplateId(template.id)}
                    >
                      <img
                        src={getFullUrl(template.thumbnail_url)}
                        alt={template.name}
                        className="size-full object-cover"
                      />
                      {isSelected && (
                        <div className="absolute inset-0 bg-theme-accent/15 flex items-center justify-center pointer-events-none">
                          <span className="bg-theme-accent text-white p-1 rounded-full">
                            <Check className="size-3.5 stroke-[3]" />
                          </span>
                        </div>
                      )}
                    </button>
                  );
                })}
              </div>
            </section>
          )}

          {/* Editor Action States */}
          <div className="flex flex-col gap-4">
            <input
              ref={fileInputRef}
              type="file"
              className="sr-only"
              accept="image/png,image/jpg,image/jpeg,image/webp,image/heic,image/heif"
              onChange={(event) => handlePhotoSelected(event.target.files?.[0])}
            />
            <input
              ref={cameraInputRef}
              type="file"
              className="sr-only"
              accept="image/png,image/jpg,image/jpeg,image/webp,image/heic,image/heif"
              capture="user"
              onChange={(event) => handlePhotoSelected(event.target.files?.[0])}
            />

            {error && (
              <Alert
                variant="destructive"
                className="rounded-xl border-red-500/50 bg-red-500/10 text-red-500"
              >
                <Icons.warning className="size-5" />
                <AlertTitle className="font-bold">Gagal Mengunggah</AlertTitle>
                <AlertDescription className="text-sm">{error}</AlertDescription>
              </Alert>
            )}

            {/* State A: Editing View (Photo is selected but not generated yet) */}
            {!generatedUrl && photo && (
              <div className="flex flex-col gap-4 animate-fade-in">
                {/* Zoom range controller */}
                <div className="flex flex-col gap-2 rounded-xl bg-white/5 p-4 dark:bg-black/5">
                  <div className="flex items-center justify-between text-xs font-semibold text-theme-gray">
                    <span>Perbesar Foto</span>
                    <span>{Math.round(transform.zoom * 100)}%</span>
                  </div>
                  <input
                    type="range"
                    min="1"
                    max="3"
                    step="0.01"
                    value={transform.zoom}
                    onChange={(event) => updateZoom(Number(event.target.value))}
                    className="h-1.5 w-full cursor-pointer appearance-none rounded-lg bg-white/10 accent-theme-accent dark:bg-black/10"
                    aria-label="Zoom foto"
                  />
                </div>

                {/* Reset & Change Photo buttons */}
                <div className="grid grid-cols-2 gap-3">
                  <Button
                    type="button"
                    variant="outline"
                    className="h-12 rounded-xl border-white/10 bg-white/5 text-base font-semibold hover:bg-white/10 dark:border-theme-gray/20 dark:bg-theme-gray/5 dark:text-theme-black3"
                    onClick={resetTransform}
                  >
                    <RefreshCw className="mr-2 size-4" />
                    Reset
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    className="h-12 rounded-xl border-white/10 bg-white/5 text-base font-semibold hover:bg-white/10 dark:border-theme-gray/20 dark:bg-theme-gray/5 dark:text-theme-black3"
                    onClick={() => fileInputRef.current?.click()}
                  >
                    <Upload className="mr-2 size-4" />
                    Ganti Foto
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    className="col-span-2 h-12 rounded-xl border-white/10 bg-white/5 text-base font-semibold hover:bg-white/10 dark:border-theme-gray/20 dark:bg-theme-gray/5 dark:text-theme-black3 md:hidden"
                    onClick={() => cameraInputRef.current?.click()}
                  >
                    <Camera className="mr-2 size-4" />
                    Ambil Foto
                  </Button>
                </div>

                {/* Generate Button (Next) */}
                <Button
                  type="button"
                  className="h-12 w-full rounded-xl bg-theme-accent text-base font-semibold text-white transition-all hover:bg-theme-accent/90 hover:scale-[1.01]"
                  onClick={generateImage}
                  disabled={Boolean(generateDisabledReason)}
                >
                  <Sparkles className="mr-2 size-5" />
                  {isFrameLoading ? "Memuat Frame" : "Buat Twibbon"}
                </Button>
                {generateDisabledReason ? (
                  <p className="text-center text-xs font-medium text-theme-gray">
                    {generateDisabledReason}
                  </p>
                ) : null}
              </div>
            )}

            {/* State B: Initial View (No photo uploaded yet) */}
            {!generatedUrl && !photo && (
              <div className="flex flex-col gap-3">
                <Button
                  type="button"
                  className="h-12 w-full rounded-xl bg-theme-accent text-base font-semibold text-white transition-all hover:bg-theme-accent/90 hover:scale-[1.01] shadow-lg shadow-theme-accent/20"
                  onClick={() => fileInputRef.current?.click()}
                >
                  <Upload className="mr-2 size-5" />
                  Pilih Foto Anda
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  className="h-12 w-full rounded-xl border-white/10 bg-white/5 text-base font-semibold hover:bg-white/10 dark:border-theme-gray/20 dark:bg-theme-gray/5 dark:text-theme-black3 md:hidden"
                  onClick={() => cameraInputRef.current?.click()}
                >
                  <Camera className="mr-2 size-5" />
                  Ambil Foto
                </Button>
              </div>
            )}

            {/* State C: Success/Generated View (Composed image is ready) */}
            {generatedUrl && (
              <div className="flex flex-col gap-4 animate-fade-in">
                {/* Composed file download button */}
                <Button
                  type="button"
                  className="h-12 w-full rounded-xl bg-green-600 text-base font-semibold text-white transition-all hover:scale-[1.01] hover:bg-green-500 shadow-lg shadow-green-600/20"
                  onClick={downloadImage}
                >
                  <Download className="mr-2 size-6" />
                  Unduh Twibbon
                </Button>

                {/* Share Row */}
                <div className={cn("grid gap-3", supportsNativeShare ? "grid-cols-2" : "grid-cols-1")}>
                  {supportsNativeShare ? (
                    <Button
                      type="button"
                      variant="outline"
                      className="h-12 rounded-xl border-white/10 bg-white/5 text-base font-semibold hover:bg-white/10 dark:border-theme-gray/20 dark:bg-theme-gray/5 dark:text-theme-black3"
                      onClick={nativeShareImage}
                    >
                      <Share2 className="mr-2 size-4" />
                      Share Gambar
                    </Button>
                  ) : null}
                  <Button
                    type="button"
                    variant="outline"
                    className="h-12 rounded-xl border-white/10 bg-white/5 text-base font-semibold hover:bg-white/10 dark:border-theme-gray/20 dark:bg-theme-gray/5 dark:text-theme-black3"
                    onClick={shareLink}
                  >
                    <Copy className="mr-2 size-4" />
                    Share Link
                  </Button>
                </div>

                {/* Back to Edit Button */}
                <Button
                  type="button"
                  variant="link"
                  className="text-sm font-semibold text-theme-gray hover:text-white dark:hover:text-theme-black3"
                  onClick={resetGenerated}
                >
                  Edit Kembali
                </Button>
              </div>
            )}
          </div>
        </div>

        <div className="flex flex-col gap-6 rounded-2xl border border-white/10 bg-theme-black2 p-4 dark:border-theme-gray/20 dark:bg-white sm:p-6">
          <section className="flex flex-col gap-4">
            <div className="flex flex-col gap-2">
              <h1 className="text-3xl font-black tracking-tight text-white dark:text-theme-black3 sm:text-4xl">
                {initialData.event.title}
              </h1>
              <span className="text-sm font-semibold text-theme-accent uppercase tracking-wider">
                Twibbon
              </span>
            </div>

            {initialData.twibbon.description && (
              <p className="text-sm text-theme-gray leading-relaxed whitespace-pre-wrap border-t border-white/10 pt-4 dark:border-theme-gray/20">
                {initialData.twibbon.description}
              </p>
            )}

            {/* Organizer Context */}
            <div className="flex flex-wrap items-center gap-x-4 gap-y-2 text-sm font-medium text-theme-gray border-t border-white/10 pt-4 dark:border-theme-gray/20">
              <span className="inline-flex items-center gap-2">
                {initialData.event.organizer.profile_photo_url ? (
                  <img
                    src={getFullUrl(initialData.event.organizer.profile_photo_url)}
                    alt={initialData.event.organizer.name}
                    className="size-6 rounded-full object-cover"
                  />
                ) : (
                  <span className="flex size-6 items-center justify-center rounded-full bg-theme-accent/20 text-theme-accent">
                    <Icons.user className="size-3.5" />
                  </span>
                )}
                <span className="text-white dark:text-theme-black3">
                  {initialData.event.organizer.name}
                </span>
              </span>
            </div>
          </section>

          <section className="flex flex-col gap-3 border-t border-white/10 pt-6 dark:border-theme-gray/20">
            <h2 className="text-base font-bold text-white dark:text-theme-black3">Link Twibbon</h2>
            <div className="flex gap-2">
              <Input
                readOnly
                value={shareUrl}
                className="h-11 border-theme-accent/20 bg-theme-accent/10 font-mono text-sm text-theme-accent dark:border-theme-accent/30 dark:bg-theme-accent/10 dark:text-theme-accent"
              />
              <Button
                type="button"
                className="h-12 rounded-xl bg-theme-accent px-4 text-base font-semibold text-white hover:bg-theme-accent/90"
                onClick={shareLink}
              >
                Salin
              </Button>
            </div>
          </section>

        </div>
      </div>
    </main>
  );
}
